import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import ChartFrame from "@/components/ChartFrame";
import { Trash2 } from "lucide-react";
import { FIELDS, sumSubscores, numericValue } from "./testConfig";
import { TOOLTIP_STYLE, TICK_MINI, DOT_BLUE, INIT_DIM_MD } from "@/lib/chartStyles";

export default function TestResultGroup({ name, entries, cfg, onDelete }) {
  const trend = [...entries].reverse()
    .map((t) => ({ date: new Date(t.date).toLocaleDateString("en-IE", { day: "numeric", month: "short" }), value: numericValue(t, cfg) }))
    .filter((d) => d.value !== null);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-['Outfit'] font-bold text-slate-800">{name}</h4>
        <span className="text-xs text-slate-400">{entries.length} record{entries.length > 1 ? "s" : ""}</span>
      </div>
      {trend.length > 1 && (
        <ChartFrame className="h-24 mb-3 -ml-2">
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={INIT_DIM_MD}>
            <LineChart data={trend}>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <XAxis dataKey="date" tick={TICK_MINI} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Line type="monotone" dataKey="value" stroke="#6C8EBF" strokeWidth={2.5} dot={DOT_BLUE} />
            </LineChart>
          </ResponsiveContainer>
        </ChartFrame>
      )}
      <div className="space-y-2">
        {entries.map((t) => (
          <div key={t.id} data-testid={`test-row-${t.id}`} className="flex items-start gap-3 text-sm border-t border-slate-50 pt-2 first:border-0">
            <span className="text-slate-400 w-20 shrink-0 pt-0.5">{new Date(t.date).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "2-digit" })}</span>
            <div className="flex-1 flex flex-wrap gap-x-4 gap-y-0.5">
              {t.window && <span className="text-[11px] font-bold uppercase tracking-wide text-[#6C8EBF] bg-[#6C8EBF]/10 rounded-full px-2 py-0.5">{t.window}</span>}
              {t.risk_band && <span className="text-[11px] font-bold uppercase tracking-wide text-[#b07d3a] bg-[#b07d3a]/10 rounded-full px-2 py-0.5">{t.risk_band}</span>}
              {t.subscores && Object.entries(t.subscores).map(([k, v]) => (
                <span key={k} className="text-slate-600"><span className="text-slate-400">{k}:</span> <b>{v}</b></span>
              ))}
              {cfg?.showTotal && t.subscores && Object.keys(t.subscores).length > 0 && (
                <span className="text-slate-600"><span className="text-slate-400">Total:</span> <b>{sumSubscores(t.subscores)}</b></span>
              )}
              {FIELDS.map(([k, label]) => t[k] ? <span key={k} className="text-slate-600"><span className="text-slate-400">{label}:</span> <b>{t[k]}</b></span> : null)}
              {t.notes && <span className="text-slate-400 italic">{t.notes}</span>}
            </div>
            <button data-testid={`delete-test-${t.id}`} onClick={() => onDelete(t.id)} className="text-slate-300 hover:text-red-500 pt-0.5"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
